import { BASE_API_URL_V2 } from "@/constants";
import fpFetch from "@/services/fpFetch";
import { preflightStaffIngress } from "@/services/staffIngress";
import useFileTrayStore, {
  FileWithStatus,
  DuplicateUploadInfo,
} from "../useFileTrayStore";
import { useUploadSession } from "../TrayViewer";
import { productDelay } from "@/utils/productTimers";

const CHUNKED_THRESHOLD = 100 * 1024 * 1024; // 100MB — files above this use multipart

/** The name the server will see for a file: its path inside a dropped
 * folder when there is one, else its name. */
const uploadName = (file: File): string =>
  file.webkitRelativePath ||
  (file as File & { _customRelativePath?: string })._customRelativePath ||
  file.name;

/**
 * One staff ingress check for the whole batch before a single file is
 * sent (#10718): the manifest of every name plus a handful of the files as
 * samples goes to the server, which asks the model once and hands back a
 * batch token the per-file uploads carry so they ask nothing more. A 428
 * that reaches here is a refusal the staff member already declined in the
 * modal (fpFetch answers it first): every file fails and nothing is
 * uploaded.
 *
 * @returns the batch token to send with each file, or null to stop
 */
const preflightBatch = async (
  files: File[],
  trayFiles: FileWithStatus[]
): Promise<{ proceed: boolean; batch: string | null }> => {
  const outcome = await preflightStaffIngress(
    "document",
    files.map((file) => ({ name: uploadName(file), file }))
  );
  if (outcome.proceed) return outcome;

  const updateFile = useFileTrayStore.getState().updateFile;
  for (const file of files) {
    const trayFile = trayFiles.find(
      (tf) => tf.originalFile === file || tf.name === file.name
    );
    if (trayFile) {
      updateFile({
        ...trayFile,
        status: "failed",
        error: "File upload failed with status 428",
        originalFile: trayFile.originalFile,
      });
    }
  }
  return outcome;
};

/**
 * Upload a large file using S3 multipart presigned URLs.
 * 1. Init multipart upload (get presigned URLs)
 * 2. Upload each chunk directly to S3 via PUT
 * 3. Complete the multipart upload (creates blob + task)
 */
const chunkedUpload = async (
  file: File,
  metadata: {
    filename: string;
    uploaded_file_path: string; // JSON array of folder segments
    useFilesystem: boolean;
    isFolderUpload: boolean;
    driveId?: string;
    batchSize: number;
    ingressBatch: string | null;
  },
  onProgress: (progress: number) => void
): Promise<{ status: number; data: Record<string, unknown> }> => {
  // 1. Init multipart upload — backend determines chunk size and part count
  const initResponse = await fpFetch(
    BASE_API_URL_V2 + "/api/v2/users/files/upload/multipart/init",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        filename: metadata.filename,
        file_size: file.size,
        uploaded_file_path: metadata.uploaded_file_path,
        useFilesystem: metadata.useFilesystem,
        driveId: metadata.driveId,
      }),
    }
  );

  if (!initResponse.ok) {
    throw new Error(`Multipart init failed with status ${initResponse.status}`);
  }

  const { upload_id, presigned_urls, chunk_size } = await initResponse.json();

  // 2. Upload each chunk directly to S3 via presigned PUT URLs
  const parts: { ETag: string; PartNumber: number }[] = [];
  let uploadedBytes = 0;
  const totalParts = presigned_urls.length;

  for (let i = 0; i < totalParts; i++) {
    const start = i * chunk_size;
    const end = Math.min(start + chunk_size, file.size);
    const chunk = file.slice(start, end);

    const putResponse = await fetch(presigned_urls[i], {
      method: "PUT",
      body: chunk,
    });

    if (!putResponse.ok) {
      await fpFetch(
        BASE_API_URL_V2 + "/api/v2/users/files/upload/multipart/abort",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ upload_id }),
        }
      );
      throw new Error(
        `Chunk ${i + 1} upload failed with status ${putResponse.status}`
      );
    }

    const etag = putResponse.headers.get("ETag");
    if (!etag) {
      throw new Error(`No ETag returned for chunk ${i + 1}`);
    }
    parts.push({ ETag: etag, PartNumber: i + 1 });

    uploadedBytes += end - start;
    onProgress(uploadedBytes / file.size);
  }

  // 3. Complete multipart upload (hash already computed server-side)
  const completeResponse = await fpFetch(
    BASE_API_URL_V2 + "/api/v2/users/files/upload/multipart/complete",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        upload_id,
        parts,
        filename: metadata.filename,
        uploaded_file_path: metadata.uploaded_file_path,
        useFilesystem: metadata.useFilesystem,
        isFolderUpload: metadata.isFolderUpload,
        driveId: metadata.driveId,
        batchSize: metadata.batchSize,
        ingress_batch: metadata.ingressBatch,
      }),
    }
  );

  const data = await completeResponse.json();
  return { status: completeResponse.status, data };
};

/**
 * Upload a single file to the server with retry logic
 * @param file File to upload
 * @param parentFilePath Parent folder path segments (never joined: a name can contain "/")
 * @param metaData Additional metadata for upload
 * @param fileIndex Index of the file in the tray (or -1 to find by originalFile)
 * @param trayFiles Array of files in the tray
 * @param batchSize Batch size for upload
 * @param maxRetries Maximum number of retry attempts (default: 3)
 * @returns Uploaded file data
 */
const uploadSingleFile = async (
  file: File,
  parentFilePath: string[],
  metaData: {
    orgId?: string;
    workspaceId?: string;
    useFilesystem?: boolean;
    driveId?: string;
    isFolderUpload?: boolean;
    entityId?: string;
  },
  fileIndex: number,
  trayFiles: FileWithStatus[],
  batchSize: number,
  ingressBatch: string | null,
  maxRetries: number = 3
) => {
  const updateFile = useFileTrayStore.getState().updateFile;

  // Find the matching file in trayFiles by originalFile if fileIndex is invalid
  let trayFile: FileWithStatus | undefined;
  if (fileIndex >= 0 && fileIndex < trayFiles.length) {
    trayFile = trayFiles[fileIndex];
  } else {
    // Find by matching originalFile
    trayFile = trayFiles.find(
      (tf) => tf.originalFile === file || tf.name === file.name
    );
  }

  // Fallback: use the first file if we can't find a match (shouldn't happen)
  if (!trayFile && trayFiles.length > 0) {
    trayFile = trayFiles[0];
  }

  let lastError: unknown = null;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      // Determine if this is a folder upload
      const isFolderUpload = !!(
        file.webkitRelativePath || (file as any)._customRelativePath
      );

      // Build the uploaded_file_path: the JSON array of folder segments,
      // which is how the upload routes take a path that may hold "/" in a name.
      let folderSegments: string[];
      let actualFileName = file.name;

      if (isFolderUpload) {
        // For folder uploads, extract just the directory path without the filename
        const relativePath =
          file.webkitRelativePath || (file as any)._customRelativePath;
        // webkitRelativePath is the browser's own "/"-joined contract.
        const pathParts: string[] = relativePath.split("/");

        // Extract the actual filename (last segment of the relative path)
        // This is important because in Firefox, file.name includes the folder structure
        // e.g., "testing ah folder/Test interaction 12.pdf" instead of just "Test interaction 12.pdf"
        const baseFileName = pathParts[pathParts.length - 1];
        actualFileName = baseFileName;

        // Remove the filename from the end to get just the directory path
        pathParts.pop();

        folderSegments = [...parentFilePath, ...pathParts];
      } else {
        // For regular file uploads, uploaded_file_path should be just the directory path
        // The filename comes from the uploaded_file field, not the path
        folderSegments = [...parentFilePath];
      }
      const uploaded_file_path = JSON.stringify(folderSegments);

      const formData = new FormData();
      formData.append("uploaded_file_path", uploaded_file_path);

      // For folder uploads, create a new File object with just the base filename
      // This ensures the backend receives the correct filename regardless of browser behavior
      // Chrome/Firefox send webkitRelativePath as the filename in FormData multipart, not file.name
      if (isFolderUpload) {
        const correctedFile = new File([file], actualFileName, {
          type: file.type,
          lastModified: file.lastModified,
        });
        formData.append("uploaded_file", correctedFile);
      } else {
        formData.append("uploaded_file", file);
      }
      if (metaData.orgId) {
        formData.append("organizationId", metaData.orgId);
      } else if (metaData.workspaceId) {
        formData.append("workspaceId", metaData.workspaceId);
      }
      if (metaData.useFilesystem) {
        formData.append("useFilesystem", "true");
      }
      if (metaData.driveId) {
        formData.append("drive_id", metaData.driveId);
      }
      if (isFolderUpload) {
        formData.append("isFolderUpload", "true");
      }
      formData.append("batchSize", String(batchSize));
      if (ingressBatch) formData.append("ingress_batch", ingressBatch);

      let status: number;
      let data: Record<string, unknown>;

      if (file.size > CHUNKED_THRESHOLD) {
        // Large file — show progress
        const uploadStartedAt = Date.now();
        const totalBytes = file.size;
        if (trayFile) {
          updateFile({
            ...trayFile,
            status: "uploading",
            progress: 0,
            uploadStartedAt,
            totalBytes,
            originalFile: trayFile.originalFile,
          });
        }

        const reportProgress = (progress: number) => {
          if (trayFile) {
            updateFile({
              ...trayFile,
              status: "uploading",
              progress,
              uploadStartedAt,
              totalBytes,
              originalFile: trayFile.originalFile,
            });
          }
        };
        // Large file — use S3 multipart chunked upload
        const result = await chunkedUpload(
          file,
          {
            filename: actualFileName,
            uploaded_file_path: uploaded_file_path,
            useFilesystem: Boolean(metaData.useFilesystem),
            isFolderUpload,
            driveId: metaData.driveId,
            batchSize,
            ingressBatch,
          },
          reportProgress
        );
        status = result.status;
        data = result.data;
      } else {
        // Small file — use regular single-request upload (no progress tracking)
        const response = await fpFetch(
          BASE_API_URL_V2 + "/api/v2/users/files/upload",
          {
            method: "POST",
            body: formData,
          }
        );
        status = response?.status ?? 0;
        data = await response.json();
      }

      if (status !== 200 && status !== 409) {
        const errorMessage = `File upload failed with status ${status}`;
        lastError = new Error(errorMessage);

        // A refusal the staff member answered (the ingress check's 428,
        // #10718) is final: it is returned here, not thrown, because the
        // catch below retries anything thrown inside this try.
        if (status === 428) {
          if (trayFile) {
            updateFile({
              ...trayFile,
              status: "failed",
              error: errorMessage,
              originalFile: trayFile.originalFile,
            });
          }
          return {
            ...file,
            status: "failed",
            error: errorMessage,
          };
        }

        if (attempt < maxRetries) {
          console.warn(
            `Upload attempt ${attempt} failed for ${file.name}, retrying...`
          );
          await productDelay(1000 * attempt);
          continue;
        }

        throw lastError;
      }

      if (data._id) {
        useUploadSession
          .getState()
          .addUploadedFile(String(data._id), metaData.entityId);
      }

      if (status === 409) {
        const dupInfo: DuplicateUploadInfo = {
          uploadedName: file.name,
          existingBlobId: String(data.existing_blob_id || data.blob_id || ""),
          existingName: String(data.existing_name || file.name),
          existingPath: (data.existing_path as string[]) || [],
        };
        useFileTrayStore.getState().addDuplicateUpload(dupInfo);

        if (trayFile) {
          updateFile({
            ...trayFile,
            ...data,
            status: "duplicate" as const,
            duplicate: true,
            error: undefined,
            existingBlobId: dupInfo.existingBlobId,
            existingPath: dupInfo.existingPath,
            existingName: dupInfo.existingName,
            originalFile: trayFile.originalFile,
          });
        }
        return {
          ...file,
          ...data,
          status: "duplicate",
          duplicate: true,
          error: undefined,
        };
      }

      if (trayFile) {
        updateFile({
          ...trayFile,
          ...data,
          status: "completed",
          error: undefined,
          originalFile: trayFile.originalFile,
        });
      }
      return {
        ...file,
        ...data,
        status: "completed",
        error: undefined,
      };
    } catch (error: unknown) {
      lastError = error;

      if (attempt < maxRetries) {
        console.warn(
          `Upload attempt ${attempt} failed for ${file.name}, retrying...`
        );
        await productDelay(1000 * attempt);
        continue;
      }

      const errorMessage =
        lastError instanceof Error ? lastError.message : "Unknown error";
      console.error(
        `Upload failed for ${file.name} after ${maxRetries} attempts:`,
        errorMessage
      );

      if (trayFile) {
        updateFile({
          ...trayFile,
          status: "failed",
          error: errorMessage,
          originalFile: trayFile.originalFile,
        });
      }
      return {
        ...file,
        status: "failed",
        error: errorMessage,
      };
    }
  }

  const errorMessage = lastError?.message || "Unknown error";
  if (trayFile) {
    updateFile({
      ...trayFile,
      status: "failed",
      error: errorMessage,
      originalFile: trayFile.originalFile,
    });
  }
  return {
    ...file,
    status: "failed",
    error: errorMessage,
  };
};

/**
 * Process files in batches
 * @param files Array of files to upload
 * @param batchSize Number of files to process in each batch
 * @param processFn Function to process each batch
 * @returns Results from all batches
 */
const processBatches = async <T, R>(
  items: T[],
  batchSize: number,
  processFn: (batch: T[]) => Promise<R[]>
): Promise<R[]> => {
  const results: R[] = [];

  // Process files in batches
  for (let i = 0; i < items.length; i += batchSize) {
    const batch = items.slice(i, i + batchSize);
    const batchResults = await processFn(batch);
    results.push(...batchResults);
  }

  return results;
};

const uploadFiles = async (
  files: File[],
  parentFilePath: string[],
  metaData: {
    orgId?: string;
    workspaceId?: string;
    useFilesystem?: boolean;
    driveId?: string;
    isFolderUpload?: boolean;
    entityId?: string;
  },
  trayFiles: FileWithStatus[]
) => {
  // Default batch size of 10
  const BATCH_SIZE = 10;

  const preflight = await preflightBatch(files, trayFiles);
  if (!preflight.proceed) {
    return files.map((file) => ({
      ...file,
      status: "failed",
      error: "File upload failed with status 428",
    }));
  }

  const webUploadBatchSize = files.length;

  // Process files in batches of BATCH_SIZE
  const uploadedFiles = await processBatches(
    files,
    BATCH_SIZE,
    async (batch) => {
      // For each batch, create array of promises to upload each file
      const batchPromises = batch.map((file, batchIndex) => {
        // Try to find the matching file in trayFiles by originalFile first
        // This is important for retry scenarios where the fileIndex might not match
        const trayFileIndex = trayFiles.findIndex(
          (tf) => tf.originalFile === file || (tf.name === file.name && !tf._id)
        );

        // If not found by originalFile, try to find by index in the files array
        // This works for initial uploads where files are in the same order
        const fileIndex =
          trayFileIndex >= 0 ? trayFileIndex : files.indexOf(file);

        return uploadSingleFile(
          file,
          parentFilePath,
          metaData,
          fileIndex,
          trayFiles,
          webUploadBatchSize,
          preflight.batch
        );
      });

      // Wait for all files in this batch to complete
      return await Promise.all(batchPromises);
    }
  );

  return uploadedFiles;
};

export default uploadFiles;
